import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { XCircle } from '../assets/AppIcons';
import { FormActionsContext } from './FormActionsContext';
import './create-form-dialog.css';
import IconAction from './IconAction';
import IconActions from './IconActions';

export function CreateFormDialog({
  open,
  onOpenChange,
  icon: Icon,
  title,
  description,
  children,
  footer,
  initialFocusRef,
  portal = false,
  busy = false,
  className = '',
}) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef(null);
  const initialFocus = useRef(initialFocusRef);
  initialFocus.current = initialFocusRef;
  const [actionHost, setActionHost] = useState(null);
  const restoreFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    if (!restoreFocusRef.current) {
      restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const firstField =
      initialFocus.current?.current ||
      dialogRef.current?.querySelector(
        'input:not([type="hidden"]):not([type="file"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="combobox"]:not([disabled])',
      );
    (firstField || dialogRef.current)?.focus?.();
    return () => {
      document.body.style.overflow = previousOverflow;
      restoreFocusRef.current?.focus?.();
      restoreFocusRef.current = null;
    };
  }, [open]);

  if (!open) return null;

  function handleKeyDown(event) {
    if (event.defaultPrevented || !event.currentTarget.contains(event.target)) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      if (!busy) onOpenChange?.(false);
      return;
    }
    if (event.key !== 'Tab') return;
    const dialog = event.currentTarget;
    const focusable = Array.from(
      dialog.querySelectorAll(
        'button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter(
      (element) => !element.closest('[hidden], [inert]') && getComputedStyle(element).display !== 'none',
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const content = (
    <FormActionsContext.Provider value={actionHost}>
      <div
        className="create-form-dialog__overlay"
        role="presentation"
        onMouseDown={(event) => {
          if (!busy && event.target === event.currentTarget) onOpenChange?.(false);
        }}
      >
        <section
          className={`create-form-dialog ${className}`.trim()}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descriptionId : undefined}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          onMouseDown={(event) => event.stopPropagation()}
          ref={dialogRef}
        >
          <header className="create-form-dialog__header">
            <span className="create-form-dialog__icon" aria-hidden="true">
              {Icon ? <Icon size={32} /> : null}
            </span>
            <div className="create-form-dialog__heading">
              <h2 id={titleId}>{title}</h2>
              {description ? <p id={descriptionId}>{description}</p> : null}
            </div>
            <button
              type="button"
              className="create-form-dialog__close"
              aria-label="Chiudi"
              disabled={busy}
              onClick={() => onOpenChange?.(false)}
            >
              <XCircle size={32} aria-hidden="true" />
            </button>
          </header>
          <div className="create-form-dialog__body">{children}</div>
          <footer className="create-form-dialog__footer">
            <div className="form-footer-tools" ref={setActionHost} />
            <IconActions>{footer}</IconActions>
          </footer>
        </section>
      </div>
    </FormActionsContext.Provider>
  );
  return portal ? createPortal(content, document.body) : content;
}

export function CreateFormCancelButton({ children = 'Annulla', className = '', ...props }) {
  return (
    <IconAction
      type="button"
      iconKey="delete"
      tone="danger"
      className={`create-form-dialog__button create-form-dialog__button--secondary ${className}`}
      {...props}
    >
      {children}
    </IconAction>
  );
}

export function CreateFormSubmitButton({
  saving,
  children,
  savingLabel = 'Salvataggio...',
  className = '',
  ...props
}) {
  return (
    <IconAction
      type="submit"
      iconKey="save"
      className={`create-form-dialog__button create-form-dialog__button--primary ${className}`}
      {...props}
      aria-busy={saving || undefined}
      disabled={saving || props.disabled}
    >
      {saving ? savingLabel : children}
    </IconAction>
  );
}
